// src/pages/EDNote.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import {
  Save,
  ArrowLeft,
  CheckCircle,
  Sparkles,
  Copy,
} from 'lucide-react';
import { motion } from 'framer-motion';
import SmartCaptureModal from '../components/SmartCaptureModal';
import { apiPost } from '../utils/api'; // ✅ Unified version-aware API helper

// --- Reusable Text Area ---
const NoteSectionTextarea = ({
  label,
  placeholder,
  heightClass = 'h-48',
  ...props
}) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <textarea
      placeholder={placeholder}
      {...props}
      className={`w-full ${heightClass} bg-slate-100 dark:bg-zinc-800 border-none rounded-lg p-4 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
    />
  </div>
);

export default function EDNote() {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showSmartCapture, setShowSmartCapture] = useState(false);

  const [noteData, setNoteData] = useState({
    hpi: '',
    ros: '',
    exam: '',
    ap: '',
    mdm: '',
    disposition: '',
    billingSuggestion: '',
    type: 'ED Note',
  });

  const [rawTranscript, setRawTranscript] = useState('');
  const [patientName, setPatientName] = useState('');
  const [activeTab, setActiveTab] = useState('History');

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const noteTabs = ['History', 'Exam', 'MDM'];

  // ─────────────────────────────────────────────
  // Fetch patient info
  // ─────────────────────────────────────────────
  const fetchPatientName = useCallback(async () => {
    if (!patientId) return;

    const ref = doc(db, 'patients', patientId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data();
      setPatientName(`${d.firstName || ''} ${d.lastName || ''}`.trim());
    }
  }, [patientId]);

  useEffect(() => {
    const init = async () => {
      setErrorMsg('');
      fetchPatientName();

      if (noteId && noteId !== 'new') {
        const ref = doc(db, 'notes', noteId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setNoteData(prev => ({ ...prev, ...snap.data() }));
        }
      } else {
        setNoteData({
          hpi: '',
          ros: '',
          exam: '',
          ap: '',
          mdm: '',
          disposition: '',
          billingSuggestion: '',
          type: 'ED Note',
        });
      }
    };
    init();
  }, [noteId, patientId, fetchPatientName]);

  // ─────────────────────────────────────────────
  // Save note to Firestore
  // ─────────────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    setErrorMsg('');

    const summary = `
Brief HPI:
${noteData.hpi}

ROS:
${noteData.ros}

Physical Exam:
${noteData.exam}

Assessment & Plan:
${noteData.ap}

Medical Decision Making:
${noteData.mdm}

Disposition:
${noteData.disposition}

Billing Suggestion:
${noteData.billingSuggestion}
`;

    const payload = {
      ...noteData,
      summary,
      timestamp: serverTimestamp(),
      type: 'ED Note',
    };

    try {
      if (noteId && noteId !== 'new') {
        await setDoc(doc(db, 'notes', noteId), payload, { merge: true });
      } else {
        const ref = await addDoc(collection(db, 'notes'), {
          ...payload,
          ownerId: currentUser.uid,
          patientID: patientId,
        });
        navigate(`/ed-note/${patientId}/${ref.id}`, { replace: true });
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1800);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to save note.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Copy to clipboard
  // ─────────────────────────────────────────────
  const handleCopy = () => {
    const fullText = `
ED Note

Brief HPI:
${noteData.hpi}

Review of Systems:
${noteData.ros}

Physical Exam:
${noteData.exam}

Assessment & Plan:
${noteData.ap}

Medical Decision Making:
${noteData.mdm}

Disposition:
${noteData.disposition}

Billing Suggestion:
${noteData.billingSuggestion}
`;

    navigator.clipboard.writeText(fullText);
    alert('Copied to clipboard!');
  };

  // ─────────────────────────────────────────────
  // Generate ED Summary — uses /v1/generate-ed-summary
  // ─────────────────────────────────────────────
  const handleGenerateSummary = async transcript => {
    if (!transcript?.trim()) return;

    setIsGenerating(true);
    setErrorMsg('');

    try {
      const result = await apiPost('/generate-ed-summary', {
        text: transcript,
      });

      setNoteData(prev => ({
        ...prev,
        ap: result.assessment_and_plan || prev.ap,
        mdm: result.mdm || prev.mdm,
        disposition:
          result.disposition_recommendation || prev.disposition,
        billingSuggestion:
          result.billing_level_suggestion || prev.billingSuggestion,
      }));
    } catch (err) {
      setErrorMsg(
        err?.message || 'Failed to generate ED summary.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // ─────────────────────────────────────────────
  // Smart Capture → /v1/format-ed-text
  // ─────────────────────────────────────────────
  const handleSmartCaptureSubmit = async text => {
    if (!text?.trim()) return;

    try {
      const r = await apiPost('/format-ed-text', { text });

      setNoteData(prev => ({
        ...prev,
        hpi: r.hpi || prev.hpi,
        ros: r.review_of_systems || prev.ros,
        exam: r.exam || prev.exam,
        ap: r.assessment_and_plan || prev.ap,
        mdm: r.mdm || prev.mdm,
        disposition:
          r.disposition_recommendation || prev.disposition,
        billingSuggestion:
          r.billing_level_suggestion || prev.billingSuggestion,
      }));

      setActiveTab('MDM');
    } catch (err) {
      console.error(err);
      setErrorMsg(
        err?.message || 'Smart Capture formatting failed.'
      );
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800"
                >
                  <ArrowLeft size={20} />
                </button>

                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                    ED Note
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400">
                    for {patientName || 'patient'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSmartCapture(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  <Sparkles size={16} /> Smart Capture
                </button>

                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-full font-semibold hover:bg-gray-600 transition"
                >
                  <Copy size={16} /> Copy
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaving || isSaved}
                  className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                    isSaved
                      ? 'bg-green-500'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isSaved ? (
                    <>
                      <CheckCircle size={18} /> Saved!
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Save Note
                    </>
                  )}
                </button>
              </div>
            </div>

            {!!errorMsg && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errorMsg}
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Tabs */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
                {noteTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                  >
                    <span
                      className={`relative z-10 ${
                        activeTab === tab
                          ? 'text-gray-800 dark:text-white'
                          : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                      }`}
                    >
                      {tab}
                    </span>

                    {activeTab === tab && (
                      <motion.div
                        layoutId="active-pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                        transition={{
                          type: 'spring',
                          stiffness: 350,
                          damping: 30,
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* History */}
            {activeTab === 'History' && (
              <NoteSectionTextarea
                label="Encounter Transcript"
                name="rawTranscript"
                value={rawTranscript}
                onChange={e => setRawTranscript(e.target.value)}
                placeholder="Paste or dictate encounter text here."
                heightClass="h-80"
              />
            )}

            {/* Exam */}
            {activeTab === 'Exam' && (
              <NoteSectionTextarea
                label="Physical Exam"
                name="exam"
                value={noteData.exam}
                onChange={e =>
                  setNoteData(p => ({
                    ...p,
                    exam: e.target.value,
                  }))
                }
                placeholder="Vitals + focused findings…"
              />
            )}

            {/* MDM */}
            {activeTab === 'MDM' && (
              <div className="space-y-4">
                <button
                  onClick={() => handleGenerateSummary(rawTranscript)}
                  disabled={isGenerating || !rawTranscript}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full font-semibold hover:bg-red-600 transition disabled:bg-gray-400"
                >
                  {isGenerating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={16} /> Generate ED Summary
                    </>
                  )}
                </button>

                <NoteSectionTextarea
                  label="Brief HPI"
                  name="hpi"
                  value={noteData.hpi}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      hpi: e.target.value,
                    }))
                  }
                />

                <NoteSectionTextarea
                  label="Review of Systems"
                  name="ros"
                  value={noteData.ros}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      ros: e.target.value,
                    }))
                  }
                />

                <NoteSectionTextarea
                  label="Assessment & Plan"
                  name="ap"
                  value={noteData.ap}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      ap: e.target.value,
                    }))
                  }
                />

                <NoteSectionTextarea
                  label="Medical Decision Making"
                  name="mdm"
                  value={noteData.mdm}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      mdm: e.target.value,
                    }))
                  }
                />

                <NoteSectionTextarea
                  label="Disposition"
                  name="disposition"
                  value={noteData.disposition}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      disposition: e.target.value,
                    }))
                  }
                />

                <NoteSectionTextarea
                  label="Billing Suggestion"
                  name="billingSuggestion"
                  value={noteData.billingSuggestion}
                  onChange={e =>
                    setNoteData(p => ({
                      ...p,
                      billingSuggestion: e.target.value,
                    }))
                  }
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Smart Capture Modal */}
      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onTextExtracted={handleSmartCaptureSubmit}
      />
    </div>
  );
}
